import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, ChatCircle, Robot, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtTimestamp, PERIOD_OPTIONS} from './_lib';

export default function ReplyInbox() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('7d');
	const [kind, setKind] = useState('human'); // human | auto | all
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/replies?period=${period}&kind=${kind}&per_page=200`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [period, kind]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);
	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	const items = (data && data.items) || [];
	const counts = (data && data.counts) || {};

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-5xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<ChatCircle size={28} weight="fill" color="#0ea5e9"/> Reply inbox
						</h1>
						<p className="text-sm text-slate-500 mt-1">Inbound attributed by In-Reply-To. Auto-replies (out-of-office, vacation, "no longer at this address") are detected and segregated — they don't represent buying signal.</p>
					</div>
					<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
						{PERIOD_OPTIONS.map(p => (
							<button key={p.key} onClick={() => setPeriod(p.key)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
						))}
					</div>
				</div>

				<div className="flex flex-wrap gap-2 mb-6 text-xs">
					<button onClick={() => setKind('human')} className={`rounded-full px-3 py-1 font-medium ${kind==='human'?'bg-sky-100 text-sky-900 border border-sky-300':'bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100'}`}>👤 human <strong>{counts.human ?? 0}</strong></button>
					<button onClick={() => setKind('auto')} className={`rounded-full px-3 py-1 font-medium ${kind==='auto'?'bg-slate-200 text-slate-800 border border-slate-300':'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'}`}>🤖 auto-reply <strong>{counts.auto ?? 0}</strong></button>
					<button onClick={() => setKind('all')} className={`rounded-full px-3 py-1 font-medium ${kind==='all'?'bg-slate-200 text-slate-800 border border-slate-300':'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'}`}>all <strong>{counts.all ?? 0}</strong></button>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && items.length === 0 && (
					<div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
						{kind === 'human'
							? 'No human replies in this window — only auto-responders so far. Switch to "auto-reply" to inspect them.'
							: 'No replies in this window.'}
					</div>
				)}

				{!loading && items.length > 0 && (
					<div className="space-y-4">
						{items.map((r,i) => {
							const isAuto = r.is_auto_reply;
							const replyHref = `mailto:${r.from}?subject=Re: ${encodeURIComponent(r.subject||'')}&In-Reply-To=${encodeURIComponent(r.reply_message_id||'')}`;
							const cardCls = isAuto
								? 'rounded-2xl border border-slate-200 bg-slate-50/40 p-5 opacity-70'
								: 'rounded-2xl border border-sky-200 bg-white p-5 hover:border-sky-300 hover:shadow-sm transition';
							return (
								<div key={r._id || i} className={cardCls}>
									<div className="flex items-start justify-between gap-4 mb-2">
										<div className="min-w-0">
											<div className="font-semibold text-slate-900 truncate flex items-center gap-2">
												{r.from}
												{isAuto && <span className="rounded-full bg-slate-200 text-slate-600 px-2 py-0.5 text-xs font-normal flex items-center gap-1"><Robot size={10}/> auto · {r.auto_reply_reason || 'auto'}</span>}
											</div>
											<div className="text-xs text-slate-500 mt-0.5">re: <em>{r.campaign_subject || r.campaign_name || '—'}</em></div>
										</div>
										<div className="text-xs text-slate-500 whitespace-nowrap">{fmtTimestamp(r.received_at_ms)}</div>
									</div>
									<div className="text-sm text-slate-700 mb-3 leading-relaxed">{r.snippet || r.subject || ''}</div>
									<div className="flex items-center gap-3 text-xs">
										{!isAuto && <a href={replyHref} className="rounded-md bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 font-medium">Reply via mailto</a>}
										<button onClick={() => navigate(`/dashboard/campaigns/${encodeURIComponent(r.campaign_id)}`)} className="text-emerald-700 hover:text-emerald-900">↗ campaign detail</button>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
